import { Controller } from "@hotwired/stimulus"

/**
 * Watchlist Details Controller
 *
 * Handles interactions for watchlist screening results display:
 * - Toggle expandable details panels for watchlist hits
 * - Trigger manual review request
 * - Proceed with application workflow
 * - Download compliance report
 * - Mobile-first keyboard navigation support
 */
export default class extends Controller {
  static targets = [
    "toggleButton",
    "toggleIcon",
    "detailsPanel"
  ]

  static values = {
    reviewUrl: String,
    downloadUrl: String,
    proceedUrl: String
  }

  connect() {
    // Setup keyboard navigation for accessibility
    this.setupKeyboardNavigation()
  }

  disconnect() {
    // Cleanup event listeners if needed
  }

  /**
   * Toggle watchlist details panel
   * @param {Event} event - Click event from toggle button
   */
  toggle(event) {
    event.preventDefault()

    const button = event.currentTarget
    const watchlistId = button.dataset.watchlistId
    const isExpanded = button.getAttribute('aria-expanded') === 'true'

    // Find the corresponding panel and icon
    const panel = this.detailsPanelTargets.find(p =>
      p.id.includes(watchlistId?.toLowerCase()?.replace(/[^a-z0-9]/g, '') || '')
    )
    const icon = button.querySelector('[data-watchlist-details-target="toggleIcon"]')

    if (!panel) return

    // Toggle panel visibility
    if (isExpanded) {
      // Collapse
      this.collapsePanel(button, panel, icon)
    } else {
      // Expand
      this.expandPanel(button, panel, icon)
    }
  }

  /**
   * Expand details panel with animation
   */
  expandPanel(button, panel, icon) {
    // Update ARIA state
    button.setAttribute('aria-expanded', 'true')

    // Show panel with fade-in
    panel.classList.remove('hidden')

    // Trigger reflow for animation
    panel.offsetHeight

    // Rotate icon
    if (icon) {
      icon.style.transform = 'rotate(180deg)'
    }

    // Announce to screen readers
    this.announceToScreenReader('Details expanded')

    // Update button text
    const buttonText = button.querySelector('span')
    if (buttonText) {
      buttonText.textContent = 'Hide Details'
    }

    // Scroll to panel if needed (mobile-friendly)
    if (this.isMobile()) {
      setTimeout(() => {
        this.scrollToElement(panel)
      }, 100)
    }
  }

  /**
   * Collapse details panel with animation
   */
  collapsePanel(button, panel, icon) {
    // Update ARIA state
    button.setAttribute('aria-expanded', 'false')

    // Hide panel
    panel.classList.add('hidden')

    // Rotate icon back
    if (icon) {
      icon.style.transform = 'rotate(0deg)'
    }

    // Announce to screen readers
    this.announceToScreenReader('Details collapsed')

    // Update button text
    const buttonText = button.querySelector('span')
    if (buttonText) {
      buttonText.textContent = 'View Details'
    }
  }

  /**
   * Request manual review for watchlist hits
   * @param {Event} event - Click event
   */
  async requestReview(event) {
    event.preventDefault()

    // Disable button to prevent double-clicks
    const button = event.currentTarget
    const originalText = button.innerHTML
    button.disabled = true
    button.innerHTML = `
      <svg class="animate-spin h-5 w-5 text-white mr-2" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Requesting Review...
    `

    try {
      // If reviewUrl is configured, make API call
      if (this.hasReviewUrlValue && this.reviewUrlValue) {
        const response = await fetch(this.reviewUrlValue, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCsrfToken(),
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to submit review request')
        }

        // Show success message
        this.showSuccessMessage('Manual review requested successfully. You will receive an email notification.')
      } else {
        // Fallback: Show manual review instructions
        this.showManualReviewInstructions()
      }
    } catch (error) {
      console.error('Review request error:', error)

      // Re-enable button
      button.disabled = false
      button.innerHTML = originalText

      // Show error message
      this.showErrorMessage('Failed to submit review request. Please try again or contact support.')
    }
  }

  /**
   * Proceed with application workflow
   * @param {Event} event - Click event
   */
  proceedWithApplication(event) {
    event.preventDefault()

    // If proceedUrl is configured, navigate there
    if (this.hasProceedUrlValue && this.proceedUrlValue) {
      window.location.href = this.proceedUrlValue
    } else {
      // Emit event for parent controller to handle
      this.dispatch('proceed', { detail: { cleared: true } })

      // Show confirmation
      this.showSuccessMessage('Watchlist screening cleared. Proceeding with application.')
    }
  }

  /**
   * Download compliance report
   * @param {Event} event - Click event
   */
  async downloadReport(event) {
    event.preventDefault()

    const button = event.currentTarget
    const originalText = button.innerHTML

    // Show loading state
    button.disabled = true
    button.innerHTML = `
      <svg class="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Generating...
    `

    try {
      if (this.hasDownloadUrlValue && this.downloadUrlValue) {
        // Trigger download
        window.location.href = this.downloadUrlValue

        // Show success feedback
        setTimeout(() => {
          button.disabled = false
          button.innerHTML = originalText
          this.showSuccessMessage('Report download started.')
        }, 1000)
      } else {
        // No download URL - show message
        button.disabled = false
        button.innerHTML = originalText
        this.showErrorMessage('Report download not available yet.')
      }
    } catch (error) {
      console.error('Download error:', error)
      button.disabled = false
      button.innerHTML = originalText
      this.showErrorMessage('Failed to download report. Please try again.')
    }
  }

  /**
   * Setup keyboard navigation for accessibility
   */
  setupKeyboardNavigation() {
    // Allow Enter/Space to toggle panels
    this.toggleButtonTargets.forEach(button => {
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          button.click()
        }
      })
    })
  }

  /**
   * Show manual review instructions modal/message
   */
  showManualReviewInstructions() {
    const message = `
      <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
            <div>
              <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Manual Review Requested
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Your application has been flagged for manual compliance review due to watchlist matches.
                  </p>
                  <p class="mt-2 text-sm text-gray-500">
                    Our compliance team will review within 1-2 business days and contact you via email.
                  </p>
                </div>
              </div>
            </div>
            <div class="mt-5 sm:mt-6">
              <button type="button" onclick="this.closest('.fixed').remove()" class="inline-flex justify-center w-full rounded-lg border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:text-sm">
                Got It
              </button>
            </div>
          </div>
        </div>
      </div>
    `

    document.body.insertAdjacentHTML('beforeend', message)
  }

  /**
   * Show success toast message
   */
  showSuccessMessage(text) {
    this.showToast(text, 'success')
  }

  /**
   * Show error toast message
   */
  showErrorMessage(text) {
    this.showToast(text, 'error')
  }

  /**
   * Show toast notification
   */
  showToast(text, type = 'info') {
    const bgColor = type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-blue-600'

    const toast = document.createElement('div')
    toast.className = `fixed bottom-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-opacity duration-300 text-sm sm:text-base max-w-sm`
    toast.textContent = text
    toast.style.opacity = '0'

    document.body.appendChild(toast)

    // Fade in
    setTimeout(() => {
      toast.style.opacity = '1'
    }, 10)

    // Fade out and remove
    setTimeout(() => {
      toast.style.opacity = '0'
      setTimeout(() => {
        document.body.removeChild(toast)
      }, 300)
    }, 5000)
  }

  /**
   * Get CSRF token from meta tag
   */
  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  /**
   * Detect mobile device
   */
  isMobile() {
    return window.innerWidth < 768
  }

  /**
   * Smooth scroll to element
   */
  scrollToElement(element) {
    if (element) {
      const offset = this.isMobile() ? 80 : 120
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  /**
   * Announce to screen readers (accessibility)
   */
  announceToScreenReader(message) {
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'status')
    announcement.setAttribute('aria-live', 'polite')
    announcement.className = 'sr-only'
    announcement.textContent = message

    document.body.appendChild(announcement)

    setTimeout(() => {
      document.body.removeChild(announcement)
    }, 1000)
  }
}
